import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dating as datingApi, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import SiteSections from '../components/SiteSections';
import {
  Heart, X, MapPin, Loader2, MessageCircle, Sparkles, Users, Settings as SettingsIcon,
  Camera, Send, ArrowLeft, Save, User, Plus, Video as VideoIcon, ChevronLeft, ChevronRight,
  Trash2, ShieldAlert, Ban, Flag, BookOpen, BadgeCheck, ShieldQuestion,
} from 'lucide-react';

const TABS = [
  { key: 'browse',   label: 'Browse',   icon: Sparkles },
  { key: 'likes',    label: 'My Likes', icon: Heart },
  { key: 'matches',  label: 'Matches',  icon: Users },
  { key: 'chat',     label: 'Chat',     icon: MessageCircle },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

const GENDER_OPTIONS  = [ { v: '', label: '—' }, { v: 'male', label: 'Male' }, { v: 'female', label: 'Female' }, { v: 'nonbinary', label: 'Non-binary' }, { v: 'other', label: 'Other' } ];
const REL_OPTIONS     = [
  { v: '', label: '—' },
  { v: 'single', label: 'Single' },
  { v: 'married', label: 'Married' },
  { v: 'divorced', label: 'Divorced' },
  { v: 'separated', label: 'Separated' },
  { v: 'widowed', label: 'Widowed' },
];
const LOOKING_OPTIONS = [ { v: '', label: '—' }, { v: 'friendship', label: 'Friendship' }, { v: 'casual', label: 'Casual Dating' }, { v: 'serious', label: 'Serious Relationship' } ];
const PREF_GENDER     = [ { v: 'any', label: 'Any' }, { v: 'male', label: 'Men' }, { v: 'female', label: 'Women' }, { v: 'nonbinary', label: 'Non-binary' } ];

/* Light theme utility classes reused across sub-components */
const CARD = 'bg-white border border-gray-200 rounded-2xl shadow-sm';
const FIELD = 'w-full bg-white text-black placeholder:text-gray-400 border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none rounded-lg px-3 py-2 text-sm';
const BTN_GREEN = 'inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 text-sm disabled:opacity-60 transition-colors';
const BTN_GHOST = 'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 hover:border-green-500 hover:text-green-700 text-gray-700 px-3 py-1.5 text-sm transition-colors';
const CHIP = 'inline-block text-[11px] px-2 py-1 rounded-full bg-green-50 text-green-800 border border-green-200';

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let a = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
  return a;
}

export default function Dating() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('browse');
  const [activeMatchId, setActiveMatchId] = useState(null);

  const reload = async () => {
    try { setMe(await datingApi.me()); }
    catch { toast({ title: 'Failed to load your profile' }); }
  };
  useEffect(() => { (async () => { await reload(); setLoading(false); })(); }, []);

  const goChat = (matchId) => { setActiveMatchId(matchId); setTab('chat'); };

  if (loading) return <div className="flex items-center gap-2 text-gray-600"><Loader2 className="animate-spin" size={16}/> Loading dating…</div>;
  if (!me?.exists) return (
    <div className="bg-white text-black rounded-2xl p-4 sm:p-6 -mx-2 sm:mx-0">
      <ProfileEditor initial={{}} isOnboarding onSaved={(p) => { setMe({ exists: true, profile: p }); setTab('browse'); }} initialName={user?.name} />
    </div>
  );

  const suspended = me.profile.datingSuspended || me.profile.datingBanned;

  return (
    <div className="bg-white text-black rounded-2xl p-4 sm:p-6 -mx-2 sm:mx-0 space-y-6" data-testid="dating-portal">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-black">Dating</h1>
          <p className="text-gray-600 text-sm">Meet PBB members near you.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dating/rules" data-testid="dating-rules-link"
            className="inline-flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 font-medium">
            <BookOpen size={14}/> Community rules
          </Link>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <User size={14}/> <span className="text-green-700 font-semibold">{me.profile.name || '—'}</span>
          </div>
        </div>
      </header>

      {suspended && (
        <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 flex gap-3" data-testid="dating-suspended-banner">
          <ShieldAlert className="text-yellow-600 shrink-0 mt-0.5" size={18}/>
          <div className="text-sm text-yellow-900">
            <div className="font-semibold">
              {me.profile.datingBanned ? 'Your dating access has been permanently revoked.' : 'Your dating account is suspended pending admin review.'}
            </div>
            <p className="mt-1">
              Please review the <Link to="/dating/rules" className="underline font-medium">Community Rules</Link>. Contact support if you believe this is a mistake.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`dating-tab-${key}`}
            onClick={() => { setTab(key); if (key !== 'chat') setActiveMatchId(null); }}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap
              ${tab === key ? 'border-green-600 text-black font-semibold' : 'border-transparent text-gray-500 hover:text-black'}`}>
            <Icon size={14}/> {label}
          </button>
        ))}
      </div>

      {tab === 'browse'   && (suspended
        ? <SuspendedNotice/>
        : <BrowseTab onMatch={goChat} />)}
      {tab === 'likes'    && (suspended ? <SuspendedNotice/> : <LikesTab />)}
      {tab === 'matches'  && (suspended ? <SuspendedNotice/> : <MatchesTab onOpen={goChat} />)}
      {tab === 'chat'     && (suspended ? <SuspendedNotice/> : <ChatTab matchId={activeMatchId} onBack={() => setTab('matches')} me={me.profile} />)}
      {tab === 'settings' && <ProfileEditor initial={me.profile} onSaved={(p) => setMe({ exists: true, profile: p })} onReload={reload} />}
    </div>
  );
}

function SuspendedNotice() {
  return (
    <div className={`${CARD} p-8 text-center max-w-lg mx-auto`} data-testid="dating-suspended-notice">
      <Ban className="mx-auto text-red-500" size={26}/>
      <h3 className="mt-3 font-bold text-black">Dating access paused</h3>
      <p className="text-sm text-gray-600 mt-1">You can still edit your profile from Settings, but browsing, matches, and chat are unavailable until admin review is complete.</p>
    </div>
  );
}

/* ============================================================
   Media picker helpers
   ============================================================ */
async function uploadFile(file, expectImage = true) {
  if (!file) throw new Error('no file');
  if (expectImage && !file.type.startsWith('image/')) throw new Error('Please choose an image (JPG, PNG, WebP).');
  if (!expectImage && !file.type.startsWith('video/')) throw new Error('Please choose a video (MP4, MOV, WebM).');
  const cap = expectImage ? 12 : 60;
  if (file.size > cap * 1024 * 1024) throw new Error(`File too large — max ${cap} MB.`);
  // kind=dating triggers backend NSFW moderation. Videos still use 'business'
  // (no moderation available yet for video).
  const res = await filesApi.upload(file, expectImage ? 'dating' : 'business');
  return res.url;
}

function PhotoSlot({ url, index, onUpload, onRemove }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const u = await uploadFile(f, true); onUpload(u, index); toast({ title: `Photo ${index + 1} uploaded` }); }
    catch (err) { toast({ title: 'Upload failed', description: err?.message || err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50 group" data-testid={`dating-photo-slot-${index}`}>
      <input ref={ref} type="file" accept="image/*" hidden onChange={handle} data-testid={`dating-photo-input-${index}`} />
      {url ? (
        <>
          <img src={filesApi.urlFor(url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          {index === 0 && <div className="absolute top-1 left-1 text-[9px] bg-green-600 text-white font-semibold px-1.5 py-0.5 rounded">MAIN</div>}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/60 flex flex-col items-center justify-center gap-1 transition-opacity">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy}
              data-testid={`dating-photo-replace-${index}`}
              className="text-[10px] px-2 py-1 rounded border border-white/40 hover:border-green-400 text-white flex items-center gap-1">
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Camera size={10} />} Replace
            </button>
            <button type="button" onClick={() => onRemove(index)}
              data-testid={`dating-photo-remove-${index}`}
              className="text-[10px] px-2 py-1 rounded border border-red-400/60 hover:border-red-400 text-red-300 flex items-center gap-1">
              <Trash2 size={10} /> Remove
            </button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          data-testid={`dating-photo-picker-${index}`}
          className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-green-600 border-2 border-dashed border-gray-300 hover:border-green-500 rounded-lg disabled:opacity-60">
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
          <span className="text-[10px]">{busy ? 'Uploading…' : `Photo ${index + 1}`}</span>
        </button>
      )}
    </div>
  );
}

function VideoField({ url, onUpload, onRemove }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const u = await uploadFile(f, false); onUpload(u); toast({ title: 'Video uploaded' }); }
    catch (err) { toast({ title: 'Upload failed', description: err?.message || 'Try again' }); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="space-y-2" data-testid="dating-video-field">
      <input ref={ref} type="file" accept="video/*" hidden onChange={handle} data-testid="dating-video-input" />
      {url ? (
        <div className="space-y-2">
          <video src={filesApi.urlFor(url)} controls playsInline
            className="w-full max-h-72 rounded-lg border border-gray-200 bg-black" />
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg border border-green-600/40 text-green-700 hover:bg-green-50 inline-flex items-center gap-1 disabled:opacity-60">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <VideoIcon size={12} />} Replace
            </button>
            <button type="button" onClick={onRemove}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1">
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          data-testid="dating-video-picker"
          className="w-full aspect-video rounded-lg border-2 border-dashed border-gray-300 hover:border-green-500 flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-green-600 transition disabled:opacity-60 bg-gray-50">
          {busy ? <Loader2 size={22} className="animate-spin" /> : <VideoIcon size={22} />}
          <span className="text-xs">{busy ? 'Uploading…' : 'Add short intro video'}</span>
          <span className="text-[10px] text-gray-400">MP4/MOV/WebM · up to 60 MB</span>
        </button>
      )}
    </div>
  );
}

/* ============================================================
   Profile Editor (used for onboarding + settings)
   ============================================================ */
function ProfileEditor({ initial, onSaved, isOnboarding = false, initialName = '', onReload }) {
  const [form, setForm] = useState({
    name: initial.name || initialName || '',
    dateOfBirth: initial.dateOfBirth || '',
    gender: initial.gender || '',
    city: initial.city || '',
    bio: initial.bio || '',
    interests: (initial.interests || []).join(', '),
    photos: (initial.photos && initial.photos.length ? initial.photos : (initial.photo ? [initial.photo] : [])).slice(0, 5),
    video: initial.video || '',
    relationshipStatus: initial.relationshipStatus || '',
    lookingFor: initial.lookingFor || '',
    prefAgeMin: initial.prefAgeMin ?? 18,
    prefAgeMax: initial.prefAgeMax ?? 60,
    prefMaxDistance: initial.prefMaxDistance ?? 50,
    prefGender: initial.prefGender || 'any',
    visible: initial.visible !== false,
    ageConfirmed: !!initial.dateOfBirth,   // if DOB already saved, treat as confirmed
  });
  const [saving, setSaving] = useState(false);
  const [showBlocks, setShowBlocks] = useState(false);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const upsertPhoto = (url, i) => setForm(p => {
    const next = [...p.photos];
    while (next.length <= i) next.push('');
    next[i] = url;
    return { ...p, photos: next.filter(Boolean) };
  });
  const removePhoto = (i) => setForm(p => ({ ...p, photos: p.photos.filter((_, ix) => ix !== i) }));

  const computedAge = ageFromDob(form.dateOfBirth);

  const save = async () => {
    if (!form.dateOfBirth) { toast({ title: 'Date of birth is required to verify age' }); return; }
    if (computedAge === null || computedAge < 18) { toast({ title: 'You must be 18 or older to use Dating' }); return; }
    if (!form.ageConfirmed) { toast({ title: 'Please confirm you are 18 or older to continue' }); return; }
    if (!form.name.trim()) { toast({ title: 'Please add your first name' }); return; }
    if (form.prefAgeMin && form.prefAgeMax && Number(form.prefAgeMin) > Number(form.prefAgeMax)) {
      toast({ title: 'Preferred age range is inverted' }); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        dateOfBirth: form.dateOfBirth,
        age: computedAge,
        gender: form.gender || null,
        city: form.city.trim(),
        bio: form.bio.trim(),
        interests: form.interests.split(',').map(s => s.trim()).filter(Boolean).slice(0, 10),
        photos: form.photos.slice(0, 5),
        video: form.video || null,
        relationshipStatus: form.relationshipStatus || null,
        lookingFor: form.lookingFor || null,
        prefAgeMin: form.prefAgeMin ? Number(form.prefAgeMin) : null,
        prefAgeMax: form.prefAgeMax ? Number(form.prefAgeMax) : null,
        prefMaxDistance: form.prefMaxDistance ? Number(form.prefMaxDistance) : null,
        prefGender: form.prefGender || 'any',
        visible: form.visible,
      };
      const r = await datingApi.updateMe(payload);
      onSaved?.(r.profile);
      toast({ title: isOnboarding ? 'Profile created — welcome!' : 'Saved' });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast({ title: 'Save failed', description: typeof raw === 'string' ? raw : (raw?.message || 'Try again') });
    } finally { setSaving(false); }
  };

  const toggleVisible = async (checked) => {
    setF('visible', checked);
    if (isOnboarding) return;
    try { await datingApi.updateSettings({ visible: checked }); toast({ title: checked ? 'Profile visible' : 'Profile hidden' }); }
    catch { toast({ title: 'Failed to update visibility' }); setF('visible', !checked); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 text-black" data-testid={isOnboarding ? 'dating-onboarding' : 'dating-settings'}>
      {isOnboarding && (
        <header>
          <h1 className="text-2xl font-bold text-black">Set up your Dating profile</h1>
          <p className="text-gray-600 text-sm">One-time setup. You can edit or hide anytime from Settings.</p>
          <p className="text-xs text-green-700 mt-1">
            18+ only. By continuing you agree to the{' '}
            <Link to="/dating/rules" className="underline font-medium">Dating Community Rules</Link>.
          </p>
        </header>
      )}

      {/* Photos */}
      <section className={`${CARD} p-5 space-y-3`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-black">Photos</div>
            <div className="text-xs text-gray-600">Up to 5 photos. The first one is your main picture. Nudity is not allowed.</div>
          </div>
          <div className="text-xs text-gray-500">{form.photos.filter(Boolean).length} / 5</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <PhotoSlot key={i} url={form.photos[i]} index={i}
              onUpload={upsertPhoto} onRemove={removePhoto} />
          ))}
        </div>
      </section>

      {/* Video */}
      <section className={`${CARD} p-5 space-y-3`}>
        <div>
          <div className="text-sm font-semibold text-black">Intro video</div>
          <div className="text-xs text-gray-600">One short video (up to ~60 seconds). Optional but helps you stand out.</div>
        </div>
        <VideoField url={form.video}
          onUpload={(u) => setF('video', u)} onRemove={() => setF('video', '')} />
      </section>

      {/* About */}
      <section className={`${CARD} p-5 space-y-4`}>
        <div className="text-sm font-semibold text-black">About you</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldInput label="First name *" value={form.name} onChange={v => setF('name', v)} testId="dating-field-name" />
          <div>
            <label className="block text-xs text-gray-600 mb-1">Date of birth * (18+)</label>
            <input type="date" value={form.dateOfBirth}
              onChange={e => setF('dateOfBirth', e.target.value)}
              data-testid="dating-field-dob"
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().slice(0,10)}
              className={FIELD}/>
            {form.dateOfBirth && (
              <div className={`text-[11px] mt-1 ${computedAge >= 18 ? 'text-green-700' : 'text-red-600'}`}>
                {computedAge !== null ? (computedAge >= 18 ? `You are ${computedAge}` : `You must be 18+ (you are ${computedAge})`) : 'Enter a valid date'}
              </div>
            )}
          </div>
          <FieldSelect label="Gender" value={form.gender} onChange={v => setF('gender', v)} options={GENDER_OPTIONS} testId="dating-field-gender" />
          <FieldInput label="Location (city / state)" value={form.city} onChange={v => setF('city', v)} testId="dating-field-city" placeholder="e.g. Atlanta, GA" />
          <FieldSelect label="Relationship status" value={form.relationshipStatus} onChange={v => setF('relationshipStatus', v)} options={REL_OPTIONS} testId="dating-field-relationship" />
          <FieldSelect label="Looking for" value={form.lookingFor} onChange={v => setF('lookingFor', v)} options={LOOKING_OPTIONS} testId="dating-field-lookingfor" />
        </div>
        <FieldInput label="Interests (comma-separated)" value={form.interests} onChange={v => setF('interests', v)}
          testId="dating-field-interests" placeholder="Music, Tech, Fitness, Cooking" />
        <div>
          <label className="block text-xs text-gray-600 mb-1">Short bio</label>
          <textarea value={form.bio} onChange={e => setF('bio', e.target.value)} rows={3}
            data-testid="dating-field-bio" placeholder="Tell people what you're about. Don't share phone/email here."
            className={FIELD}/>
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={form.ageConfirmed} onChange={e => setF('ageConfirmed', e.target.checked)}
            data-testid="dating-field-age-confirm" className="mt-1 h-4 w-4 accent-green-600"/>
          <span className="text-xs text-gray-700">
            I confirm I am 18 years of age or older and I agree to the{' '}
            <Link to="/dating/rules" className="underline text-green-700 font-medium">Dating Community Rules</Link>.
          </span>
        </label>
      </section>

      {/* Preferences */}
      <section className={`${CARD} p-5 space-y-4`}>
        <div className="text-sm font-semibold text-black">Dating preferences</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <FieldInput label="Min age" type="number" min="18" value={form.prefAgeMin} onChange={v => setF('prefAgeMin', v)} testId="dating-field-prefagemin" />
          <FieldInput label="Max age" type="number" min="18" value={form.prefAgeMax} onChange={v => setF('prefAgeMax', v)} testId="dating-field-prefagemax" />
          <FieldInput label="Max distance (mi)" type="number" min="1" value={form.prefMaxDistance} onChange={v => setF('prefMaxDistance', v)} testId="dating-field-prefdistance" />
          <FieldSelect label="Show me" value={form.prefGender} onChange={v => setF('prefGender', v)} options={PREF_GENDER} testId="dating-field-prefgender" />
        </div>
      </section>

      {/* Visibility */}
      <section className={`${CARD} p-5 flex items-center justify-between gap-3`}>
        <div>
          <div className="text-sm font-semibold text-black">Visible in Browse</div>
          <div className="text-xs text-gray-600">Turn off to pause new matches. Existing chats stay open.</div>
        </div>
        <button type="button" onClick={() => toggleVisible(!form.visible)} data-testid="dating-visibility-toggle"
          className={`w-12 h-6 rounded-full transition-colors ${form.visible ? 'bg-green-600' : 'bg-gray-300'} relative`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.visible ? 'left-6' : 'left-0.5'}`}/>
        </button>
      </section>

      {/* Safety */}
      {!isOnboarding && (
        <section className={`${CARD} p-5 space-y-3`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-black">Safety & community</div>
              <div className="text-xs text-gray-600">Manage blocked users and review the community rules.</div>
            </div>
            <div className="flex gap-2">
              <Link to="/dating/rules" data-testid="dating-settings-rules"
                className={BTN_GHOST}>
                <BookOpen size={14}/> Community rules
              </Link>
              <button type="button" onClick={() => setShowBlocks(v => !v)}
                data-testid="dating-toggle-blocks"
                className={BTN_GHOST}>
                <Ban size={14}/> {showBlocks ? 'Hide blocks' : 'Blocked users'}
              </button>
            </div>
          </div>
          {showBlocks && <BlockedList onChanged={onReload}/>}
          <VerifyMeSection profile={initial} onSubmitted={onReload}/>
        </section>
      )}

      <button type="button" onClick={save} disabled={saving} data-testid={isOnboarding ? 'dating-onboard-save' : 'dating-settings-save'}
        className={`${BTN_GREEN} w-full`}>
        {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
        {isOnboarding ? 'Save & start browsing' : 'Save changes'}
      </button>
    </div>
  );
}

function BlockedList({ onChanged }) {
  const [rows, setRows] = useState(null);
  const load = async () => { try { setRows(await datingApi.listBlocks()); } catch { setRows([]); } };
  useEffect(() => { load(); }, []);
  const unblock = async (uid) => {
    try {
      await datingApi.unblock(uid);
      toast({ title: 'Unblocked' });
      await load();
      onChanged?.();
    } catch { toast({ title: 'Failed to unblock' }); }
  };
  if (rows === null) return <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading…</div>;
  if (rows.length === 0) return <div className="text-xs text-gray-500" data-testid="dating-blocks-empty">You haven't blocked anyone.</div>;
  return (
    <ul className="space-y-2" data-testid="dating-blocks-list">
      {rows.map(b => (
        <li key={b.userId} className="flex items-center gap-3 border border-gray-200 rounded-lg p-2">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 shrink-0">
            {b.photo ? <img src={filesApi.urlFor(b.photo)} alt="" className="w-full h-full object-cover"/> : <User className="w-full h-full text-gray-400 p-1"/>}
          </div>
          <div className="flex-1 min-w-0 text-sm text-black truncate">{b.name || 'PBB member'}</div>
          <button type="button" onClick={() => unblock(b.userId)} data-testid={`dating-unblock-${b.userId}`}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:border-green-500 hover:text-green-700 text-gray-700">
            Unblock
          </button>
        </li>
      ))}
    </ul>
  );
}

function VerifyMeSection({ profile, onSubmitted }) {
  const status = profile?.verificationStatus || (profile?.verified ? 'approved' : 'none');
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const submit = async (file) => {
    setBusy(true);
    try {
      const url = await uploadFile(file, true);   // NSFW-scanned + moderated
      await datingApi.submitVerification(url, '');
      toast({ title: 'Verification submitted — an admin will review shortly' });
      onSubmitted?.();
    } catch (err) {
      const raw = err?.message || err?.response?.data?.detail;
      toast({ title: 'Verification failed', description: typeof raw === 'string' ? raw : 'Try again' });
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const handle = (e) => { const f = e.target.files?.[0]; if (f) submit(f); };
  return (
    <div className="border-t border-gray-200 pt-4 mt-2" data-testid="dating-verify-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-black flex items-center gap-1.5">
            <BadgeCheck size={14} className={profile?.verified ? 'text-green-600' : 'text-gray-400'}/>
            Selfie verification
          </div>
          <div className="text-xs text-gray-600 mt-0.5 max-w-md">
            {profile?.verified
              ? 'Your profile shows a green Verified badge to other members.'
              : status === 'pending'
                ? 'Your selfie is pending admin review — usually within 24 hours.'
                : status === 'rejected'
                  ? (profile?.verificationRejectedReason || 'Your last submission was rejected. Try a clearer, well-lit selfie.')
                  : 'Submit a clear selfie of just your face. Once approved you\'ll get a green Verified badge.'}
          </div>
        </div>
        <input type="file" accept="image/*" hidden ref={fileRef} onChange={handle} data-testid="dating-verify-input"/>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy || status === 'pending' || profile?.verified}
          data-testid="dating-verify-button"
          className={`${BTN_GHOST} ${profile?.verified ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : profile?.verified ? <BadgeCheck size={14}/> : <ShieldQuestion size={14}/>}
          {profile?.verified ? 'Verified' : status === 'pending' ? 'Pending review' : 'Verify me'}
        </button>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, testId, type = 'text', min, placeholder }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <input type={type} min={min} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className={FIELD}/>
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className={FIELD}>
        {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ============================================================
   Block / Report action bar (reused on swipe card + chat header)
   ============================================================ */
function ProfileActions({ targetUserId, targetName, onBlocked, compact = false }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const doBlock = async () => {
    if (!window.confirm(`Block ${targetName || 'this user'}? You will not see them, and they will not see or message you.`)) return;
    setBusy(true);
    try {
      await datingApi.block(targetUserId);
      toast({ title: 'User blocked' });
      onBlocked?.();
    } catch { toast({ title: 'Failed to block' }); }
    finally { setBusy(false); }
  };
  const doReport = async () => {
    if (!reason.trim()) { toast({ title: 'Please add a reason.' }); return; }
    setBusy(true);
    try {
      const r = await datingApi.report(targetUserId, reason.trim());
      toast({ title: r.autoSuspended ? 'Report received — profile auto-suspended for review' : 'Report received — thanks for keeping PBB safe' });
      setReportOpen(false); setReason('');
    } catch (err) {
      toast({ title: 'Failed to report', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };
  const btn = compact ? 'text-[11px] px-2 py-1' : 'text-xs px-3 py-1.5';
  return (
    <>
      <div className="inline-flex items-center gap-2" data-testid={`dating-actions-${targetUserId}`}>
        <button type="button" onClick={() => setReportOpen(true)} disabled={busy}
          data-testid={`dating-report-${targetUserId}`}
          className={`${btn} rounded-lg border border-yellow-300 text-yellow-700 hover:bg-yellow-50 inline-flex items-center gap-1 disabled:opacity-60`}>
          <Flag size={12}/> Report
        </button>
        <button type="button" onClick={doBlock} disabled={busy}
          data-testid={`dating-block-${targetUserId}`}
          className={`${btn} rounded-lg border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-60`}>
          <Ban size={12}/> Block
        </button>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="dating-report-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
                <Flag className="text-yellow-600" size={18}/>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-black">Report {targetName || 'this user'}</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Our admin team reviews every report within 24 hours. Three reports auto-suspend the account pending review. See the <Link to="/dating/rules" className="underline text-green-700">Community Rules</Link>.
                </p>
                <textarea rows={4} value={reason} onChange={e => setReason(e.target.value)}
                  data-testid="dating-report-reason"
                  placeholder="What's happening? (harassment, fake profile, explicit content, etc.)"
                  className="mt-3 w-full bg-white text-black border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"/>
                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" onClick={() => setReportOpen(false)} disabled={busy}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:text-black">Cancel</button>
                  <button type="button" onClick={doReport} disabled={busy || !reason.trim()}
                    data-testid="dating-report-submit"
                    className={BTN_GREEN}>
                    {busy ? <Loader2 size={12} className="animate-spin"/> : <Flag size={12}/>} Submit report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================================
   Browse — swipe cards with drag gesture + like/pass buttons
   ============================================================ */
function BrowseTab({ onMatch }) {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [drag, setDrag] = useState({ x: 0, active: false, start: 0 });

  const load = async () => {
    setBusy(true);
    try { setQueue(await datingApi.browse()); setIdx(0); setPhotoIdx(0); }
    catch { toast({ title: 'Failed to load profiles' }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const p = queue?.[idx];
  useEffect(() => { setPhotoIdx(0); }, [p?.userId]);

  const advance = () => setIdx(i => i + 1);
  const swipe = async (like) => {
    if (!p || busy) return;
    setBusy(true);
    try {
      const res = await datingApi.swipe(p.userId, like);
      if (like && res.matched) {
        toast({ title: `It's a match with ${res.name || p.name}!`, description: 'Say hi in Matches → Chat.' });
        onMatch?.(res.matchId);
        return;
      }
      advance();
    } catch (err) {
      toast({ title: 'Swipe failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); setDrag({ x: 0, active: false, start: 0 }); }
  };

  const onDragStart = (e) => {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({ x: 0, active: true, start: x });
  };
  const onDragMove = (e) => {
    if (!drag.active) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag(d => ({ ...d, x: x - d.start }));
  };
  const onDragEnd = () => {
    if (!drag.active) return;
    if (drag.x >  120) swipe(true);
    else if (drag.x < -120) swipe(false);
    else setDrag({ x: 0, active: false, start: 0 });
  };

  if (queue === null) return <div className="flex items-center gap-2 text-gray-600"><Loader2 className="animate-spin" size={16}/> Loading…</div>;
  if (!p) {
    return (
      <div className={`${CARD} p-10 text-center max-w-lg mx-auto`} data-testid="dating-queue-empty">
        <Sparkles className="mx-auto text-green-600" size={26}/>
        <h3 className="mt-3 font-bold text-black">You've caught up.</h3>
        <p className="text-sm text-gray-600 mt-1">No more profiles match your filters right now. Widen your preferences or check back later.</p>
        <button onClick={load} className={`${BTN_GREEN} mt-4`}>Refresh</button>
      </div>
    );
  }

  const photos = p.photos?.length ? p.photos : (p.photo ? [p.photo] : []);
  const cur = photos[photoIdx];
  const rotation = drag.x / 20;
  const hint = drag.x > 60 ? 'LIKE' : drag.x < -60 ? 'NOPE' : '';

  return (
    <div className="max-w-md mx-auto select-none" data-testid="dating-swipe-card">
      <div
        className={`${CARD} overflow-hidden relative`}
        style={{ transform: `translateX(${drag.x}px) rotate(${rotation}deg)`, transition: drag.active ? 'none' : 'transform .25s ease' }}
        onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
        onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
      >
        <div className="relative aspect-[3/4] bg-gray-100">
          {cur ? (
            <img src={filesApi.urlFor(cur)} alt={p.name} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300"><User size={80}/></div>
          )}
          {/* Photo pager arrows */}
          {photos.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => (i - 1 + photos.length) % photos.length); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"><ChevronLeft size={16}/></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => (i + 1) % photos.length); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"><ChevronRight size={16}/></button>
              <div className="absolute top-2 left-2 right-2 flex gap-1">
                {photos.map((_, i) => (
                  <div key={i} className={`h-0.5 flex-1 rounded ${i === photoIdx ? 'bg-white' : 'bg-white/30'}`}/>
                ))}
              </div>
            </>
          )}
          {/* Video badge */}
          {p.video && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded inline-flex items-center gap-1">
              <VideoIcon size={10}/> Video
            </div>
          )}
          {/* Drag hint labels */}
          {hint && (
            <div className={`absolute top-6 ${hint === 'LIKE' ? 'left-4 border-green-500 text-green-600 rotate-[-8deg]' : 'right-4 border-red-500 text-red-500 rotate-[8deg]'} border-4 bg-white/70 px-3 py-1 rounded-lg text-xl font-black tracking-wider`}>
              {hint}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
            <div className="text-2xl font-bold text-white flex items-center gap-2 flex-wrap">
              <span>{p.name}{p.age ? `, ${p.age}` : ''}</span>
              {p.verified && (
                <span data-testid={`dating-verified-${p.userId}`} title="Selfie-verified"
                  className="inline-flex items-center gap-1 text-xs bg-green-600 text-white font-semibold px-2 py-0.5 rounded-full">
                  <BadgeCheck size={12}/> Verified
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-100 mt-1">
              {p.city && <span className="inline-flex items-center gap-1"><MapPin size={12}/> {p.city}</span>}
              {p.gender && <span className="capitalize">{p.gender}</span>}
              {p.relationshipStatus && <span className="capitalize">· {p.relationshipStatus}</span>}
            </div>
            {p.lookingFor && <div className="text-xs text-green-300 mt-1 capitalize">Looking for: {p.lookingFor}</div>}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            {p.bio && <p className="text-sm text-gray-800 flex-1">{p.bio}</p>}
            <ProfileActions targetUserId={p.userId} targetName={p.name} onBlocked={advance} compact/>
          </div>
          {p.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {p.interests.map(i => <span key={i} className={CHIP}>{i}</span>)}
            </div>
          )}
          {p.video && (
            <video src={filesApi.urlFor(p.video)} controls playsInline
              className="w-full mt-3 rounded-lg border border-gray-200 bg-black max-h-72"/>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-5 justify-center">
        <button onClick={() => swipe(false)} disabled={busy} data-testid="dating-swipe-pass"
          className="w-14 h-14 rounded-full bg-white border border-gray-300 hover:border-red-500 flex items-center justify-center text-red-500 disabled:opacity-60 shadow-sm">
          <X size={22}/>
        </button>
        <button onClick={() => swipe(true)} disabled={busy} data-testid="dating-swipe-like"
          className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center text-white shadow-lg disabled:opacity-60">
          <Heart size={26}/>
        </button>
      </div>
      <div className="text-center text-[11px] text-gray-500 mt-2">Or drag / swipe the card left to pass, right to like</div>
    </div>
  );
}

/* ============================================================
   Likes / Matches / Chat
   ============================================================ */
function LikesTab() {
  const [likes, setLikes] = useState(null);
  useEffect(() => { datingApi.myLikes().then(setLikes).catch(() => setLikes([])); }, []);
  if (likes === null) return <div className="flex items-center gap-2 text-gray-600"><Loader2 className="animate-spin" size={16}/> Loading…</div>;
  if (likes.length === 0) return (
    <div className={`${CARD} p-8 text-center`} data-testid="dating-likes-empty">
      <Heart className="mx-auto text-green-600" size={22}/>
      <p className="mt-2 text-sm text-gray-600">Profiles you like will show here while you wait for them to like you back.</p>
    </div>
  );
  return (
    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="dating-likes-list">
      {likes.map(p => (
        <li key={p.userId} className={`${CARD} overflow-hidden`}>
          <div className="aspect-square bg-gray-100 relative">
            {(p.photos?.[0] || p.photo)
              ? <img src={filesApi.urlFor(p.photos?.[0] || p.photo)} alt={p.name} className="absolute inset-0 w-full h-full object-cover"/>
              : <div className="absolute inset-0 flex items-center justify-center text-gray-300"><User size={40}/></div>}
          </div>
          <div className="p-3">
            <div className="text-sm font-semibold text-black">{p.name}{p.age ? `, ${p.age}` : ''}</div>
            <div className="text-xs text-gray-500">{p.city || '—'}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function MatchesTab({ onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { datingApi.myMatches().then(setRows).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="flex items-center gap-2 text-gray-600"><Loader2 className="animate-spin" size={16}/> Loading…</div>;
  if (rows.length === 0) return (
    <div className={`${CARD} p-8 text-center`} data-testid="dating-matches-empty">
      <Users className="mx-auto text-green-600" size={22}/>
      <p className="mt-2 text-sm text-gray-600">No matches yet. Swipe right on someone who also likes you to unlock a chat.</p>
    </div>
  );
  return (
    <ul className="space-y-3" data-testid="dating-matches-list">
      {rows.map(m => {
        const p = m.profile || {};
        const cover = p.photos?.[0] || p.photo;
        return (
          <li key={m.matchId} className={`${CARD} p-3 flex items-center gap-3`}>
            <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 shrink-0">
              {cover
                ? <img src={filesApi.urlFor(cover)} alt={p.name} className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-gray-300"><User size={22}/></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-black truncate flex items-center gap-1.5">
                <span className="truncate">{p.name}{p.age ? `, ${p.age}` : ''}</span>
                {p.verified && <BadgeCheck size={14} className="text-green-600 shrink-0" title="Verified"/>}
              </div>
              <div className="text-xs text-gray-500 truncate">{p.city || 'PBB member'}</div>
              {m.lastMessageAt
                ? <div className="text-[11px] text-gray-500">Last chat {new Date(m.lastMessageAt).toLocaleString()}</div>
                : <div className="text-[11px] text-green-700 font-medium">Say hi first!</div>}
            </div>
            <button data-testid={`dating-open-chat-${m.matchId}`} onClick={() => onOpen(m.matchId)}
              className={`${BTN_GREEN} text-xs`}>
              <MessageCircle size={12}/> Chat
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ChatTab({ matchId, onBack, me }) {
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [match, setMatch] = useState(null);
  const [blockedOut, setBlockedOut] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!matchId) return;
    let alive = true;
    // Load match metadata once so we can show the peer's name and expose block/report on the header.
    datingApi.myMatches().then(rows => {
      if (!alive) return;
      const m = rows.find(r => r.matchId === matchId);
      setMatch(m || null);
    }).catch(() => {});
    const load = async () => {
      try { const rows = await datingApi.listMessages(matchId); if (alive) setMessages(rows); }
      catch (err) {
        if (!alive) return;
        // 404 after block means the chat is gone from this user's side; show a neutral state.
        if (err?.response?.status === 404) { setMessages([]); setBlockedOut(true); }
        else setMessages([]);
      }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [matchId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const msg = await datingApi.sendMessage(matchId, body);
      setMessages(prev => [...(prev || []), msg]);
      setDraft('');
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSending(false); }
  };

  if (!matchId) return (
    <div className={`${CARD} p-8 text-center`} data-testid="dating-chat-empty">
      <MessageCircle className="mx-auto text-green-600" size={22}/>
      <p className="mt-2 text-sm text-gray-600">Pick a match from the <b>Matches</b> tab to start chatting.</p>
    </div>
  );

  const peer = match?.profile || {};

  return (
    <div className={`${CARD} flex flex-col h-[70vh]`} data-testid="dating-chat-window">
      <div className="flex items-center gap-2 p-3 border-b border-gray-200 shrink-0">
        <button onClick={onBack} className="p-1 rounded hover:bg-gray-100 text-gray-500"><ArrowLeft size={16}/></button>
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 shrink-0">
          {peer.photo || peer.photos?.[0]
            ? <img src={filesApi.urlFor(peer.photos?.[0] || peer.photo)} alt="" className="w-full h-full object-cover"/>
            : <div className="w-full h-full flex items-center justify-center text-gray-300"><User size={16}/></div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-black truncate flex items-center gap-1">
            <span className="truncate">{peer.name || 'Match chat'}</span>
            {peer.verified && <BadgeCheck size={12} className="text-green-600 shrink-0"/>}
          </div>
          <div className="text-[11px] text-gray-500 truncate">{peer.city || ''}</div>
        </div>
        {peer.userId && (
          <ProfileActions targetUserId={peer.userId} targetName={peer.name}
            onBlocked={() => { setBlockedOut(true); onBack?.(); }} compact/>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
        {blockedOut ? (
          <div className="text-center text-sm text-gray-500 mt-6">This conversation is no longer available.</div>
        ) : messages === null ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="animate-spin" size={16}/> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-gray-500 mt-6">No messages yet — send the first one!</div>
        ) : messages.map(m => {
          const mine = m.fromUserId === me?.userId;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm break-words shadow-sm ${mine ? 'bg-green-600 text-white' : 'bg-white text-black border border-gray-200'}`}>
                {m.body}
                <div className={`text-[10px] mt-0.5 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                  {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-gray-200 flex items-center gap-2 shrink-0 bg-white">
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={blockedOut}
          data-testid="dating-chat-input" placeholder={blockedOut ? 'Chat unavailable' : 'Say something nice…'}
          className="flex-1 bg-white text-black placeholder:text-gray-400 border border-gray-300 rounded-full px-4 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none disabled:bg-gray-100" />
        <button onClick={send} disabled={sending || !draft.trim() || blockedOut} data-testid="dating-chat-send"
          className="w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 disabled:opacity-60 flex items-center justify-center text-white">
          {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
        </button>
      </div>
      <SiteSections pageKey="dating" />
    </div>
  );
}
